import React from "react";
import { Mail, Share2, QrCode } from 'lucide-react';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { ensureTemplateVariant } from "../../lib/templates/normalize";
import { DEFAULT_COVER } from '@/constants/media';
import { REMOTE_FALLBACK_COVER_URL } from '@/constants/images';
import UnifiedBrandIcon from "../UnifiedBrandIcon";
import EmailSignatureModal from "@/components/EmailSignatureModal";

/* ============ Public types ============ */

export type PreviewUser = {
  displayName?: string;
  name?: string;
  position?: string;
  company?: string;
  phone?: string;
  email?: string;
  website?: string;
  bio?: string;
  location?: string;
  avatarUrl?: string; // NEW: from signup
  coverUrl?: string;  // NEW: from signup
  // social links
  instagram?: string;
  x?: string;            // Twitter / X
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  // streaming links
  spotify?: string;
  appleMusic?: string;
  soundcloud?: string;
  audiomack?: string;
};

export type PreviewVariant =
  | "professional-classic"
  | "elegant-looking"
  | "golden-view"
  | "modern-clean"
  | "classic-modular";

// NEW: template meta/spec for dynamic templates
export type TemplateMeta = {
  componentKey?: string | null;
  variant?: string | null;
  accent?: string | null;
};

export type TemplateSpec = {
  componentKey?: string | null;
  variant?: string | null;
  accent?: string | null;
  // v1 layout DSL
  layout?: Array<
    | { kind: 'text'; text?: string; className?: string }
    | { kind: 'image'; src?: string; round?: boolean; className?: string }
    | { kind: 'button'; text?: string; href?: string; className?: string }
    | { kind: 'row'; children?: TemplateSpec['layout']; className?: string }
    | { kind: 'col'; span?: number; children?: TemplateSpec['layout']; className?: string }
  >;
  // v2 blocks schema
  header?: any;
  grid?: { columns?: number; mobileColumns?: number; gap?: number | string };
  sectionCard?: any;
  blocks?: Array<any>;
  // theme & content (new)
  theme?: {
    bg?: string;              // page background
    cardBg?: string;          // card/section bg
    text?: string;            // default text
    muted?: string;           // secondary text
    accent?: string;          // accent color
    buttonBg?: string;        // small action buttons
    buttonFg?: string;
    saveBg?: string;          // Save CTA
    saveFg?: string;
    border?: string;          // section borders
  };
  content?: {
    title?: string;           // overrides name
    subtitle?: string;        // overrides position
    about?: string;           // overrides user.bio
    saveLabel?: string;       // overrides SAVE CARD
    adsTitle?: string;        // default: ADS
    adsSubtitle?: string;     // default: SPONSORED
  };
};

// Canonical variants and a minimal alias map for strict resolution
export const KNOWN_VARIANTS: PreviewVariant[] = [
  "professional-classic",
  "elegant-looking",
  "modern-clean",
  "golden-view",
  "classic-modular",
];

const ALIASES: Record<string, PreviewVariant> = {
  "royal-ocean": "professional-classic",
  "royal_ocean": "professional-classic",
  "royalocean": "professional-classic",
  classic: "professional-classic",
  elegant: "elegant-looking",
  modern: "modern-clean",
  modular: "classic-modular",
  grid: "classic-modular",
  blocks: "classic-modular",
  golden: "golden-view",
  gold: "golden-view",
  // dynamic names that may occasionally leak into static path
  "soft-blossom": "elegant-looking",
  "softblossom": "elegant-looking",
  "soft-blossom-v1": "elegant-looking",
};

export interface PreviewCardProps {
  variant: PreviewVariant;
  user: PreviewUser;
  /** If provided, overrides the template default accent color */
  accent?: string;
  /** "grid": in-tile preview (no big CTA). "full": dialog/live preview (with CTA). */
  mode?: "grid" | "full";
  className?: string;
    templateMeta?: TemplateMeta;
    templateSpec?: TemplateSpec | null;
}

/* ============ Helpers ============ */

// ---- Known components (renderers) & alias map
export const KNOWN_COMPONENT_KEYS = [
  'professional-classic',
  'elegant-looking',
  'modern-clean',
  'golden-view',
  'classic-modular',
] as const satisfies readonly PreviewVariant[];

export const COMPONENT_ALIASES: Record<string, PreviewVariant> = {
  // numeric shorthand used in some parts of the UI
  '1': 'professional-classic',
  '2': 'elegant-looking',
  '3': 'modern-clean',
  '4': 'golden-view',
  '5': 'classic-modular',

  // common synonyms
  'classic': 'professional-classic',
  'pro-classic': 'professional-classic',
  'modern': 'modern-clean',
  'clean': 'modern-clean',
  'gold': 'golden-view',
  'golden': 'golden-view',
  // Keep royal-ocean under Professional Classic for consistency
  'royal-ocean': 'professional-classic',
  'royalocean': 'professional-classic',
  'modular': 'classic-modular',
  'grid': 'classic-modular',
  'blocks': 'classic-modular',
};

export function normalizeComponentKey(input?: string): PreviewVariant | null {
  const raw = String(input || '').trim().toLowerCase();
  if (!raw) return null;
  if ((KNOWN_COMPONENT_KEYS as readonly string[]).includes(raw)) return raw as PreviewVariant;
  if (COMPONENT_ALIASES[raw]) return COMPONENT_ALIASES[raw];
  return null;
}

// Resolve variant/accent from template meta
function resolveFromMeta(meta?: TemplateMeta | null): { variant: PreviewVariant | null; accent?: string } | null {
  if (!meta) return null;
  const key = normalizeComponentKey(meta.componentKey ?? meta.variant ?? undefined);
  // Do NOT force a default here; let callers decide fallback to avoid masking meta intent
  const variant: PreviewVariant | null = key ?? null;
  const accent = meta.accent ?? undefined;
  return { variant, accent };
}

const initials = (name?: string) =>
  (name || "User")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");

const Avatar: React.FC<{ name?: string; url?: string; round?: boolean; noBorder?: boolean; className?: string }> = ({
  name,
  url,
  round = false,
  noBorder = false,
  className,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const shapeClass = round ? "rounded-full" : "rounded-xl";
  const borderClass = noBorder ? "" : "border-2 border-white";
  const sizeClass = className ? className : "h-12 w-12";
  const resolved = React.useMemo(() => normalizeUrl(url), [url]);
  return (
    <div className={`${shapeClass} ${borderClass} ${sizeClass} flex items-center justify-center bg-white shadow-sm`}>
      {resolved && !imgError ? (
        <img
          src={resolved}
          alt={name || "avatar"}
          className="block h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-gray-600 font-bold">{initials(name)}</div>
      )}
    </div>
  );
};

/** Default accent per template */
export const getDefaultAccent = (v: PreviewVariant) => {
  switch (v) {
    case "professional-classic":
      return "#E11D48"; // rose-600
    case "elegant-looking":
      return "#FB7185"; // pink-400
    case "golden-view":
      return "#F59E0B"; // amber-500
    case "modern-clean":
      return "#10B981"; // emerald-500
    case "classic-modular":
      return "#6366F1"; // indigo-500
  }
};

/** Default stock header: centralized default cover to avoid broken images */
const defaultHeader = (_v: PreviewVariant) => DEFAULT_COVER;

/** Pick the header: prefer user's cover if present, else template default */
const HEADER_HEIGHT = 200; // px - unified cover height across templates
const GRID_SCREEN_HEIGHT = 680; // unified bezel height for grid previews (desktop)
const GRID_SCREEN_HEIGHT_TABLET = 500; // medium breakpoint (reduced)
const GRID_SCREEN_HEIGHT_MOBILE = 360; // small screens (reduced)
const GOLDEN_HEADER_HEIGHT = 140; // Golden slightly smaller to match design request

const normalizeUrl = (u?: string) => {
  if (!u) return undefined;
  const s = String(u).trim();
  if (!s) return undefined;
  const base = (import.meta as any).env?.VITE_APP_BASE_URL || (import.meta as any).env?.VITE_API_BASE_URL || '';
  // If absolute but targets uploads on a different origin (e.g., localhost), rebase to our app origin to avoid mixed content
  if (/^https?:\/\//i.test(s) && s.includes('/uploads/')) {
    const idx = s.indexOf('/uploads/');
    const rel = s.slice(idx);
    return base ? `${String(base).replace(/\/$/, '')}${rel}` : rel;
  }
  // If looks like an absolute URL (non-uploads), return as-is
  if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
  // Frontend static assets should NOT be prefixed by API base
  if (s.startsWith('/images/') || s.startsWith('/assets/')) return s;
  // Server uploads need an API base; if not configured, keep relative so reverse proxy handles it
  if (s.startsWith('/uploads/')) {
    return base ? `${String(base).replace(/\/$/, '')}${s}` : s;
  }
  // If relative path and base env present, prefix it
  if (base) {
    if (s.startsWith('/')) return `${String(base).replace(/\/$/, '')}${s}`;
    // handle 'uploads/...' or other relative paths without leading slash
    return `${String(base).replace(/\/$/, '')}/${s}`;
  }
  return s.startsWith('/') ? s : `/${s}`;
};

const resolveHeader = (variant: PreviewVariant, userCover?: string) => normalizeUrl(userCover) || defaultHeader(variant);

// Robust header resolver: pre-load and fallback to DEFAULT_COVER if load fails
const useResolvedHeader = (variant: PreviewVariant, coverUrl?: string) => {
  const initial = React.useMemo(() => resolveHeader(variant, coverUrl), [variant, coverUrl]);
  const [src, setSrc] = React.useState<string>(initial);

  React.useEffect(() => {
    const candidate = resolveHeader(variant, coverUrl);
    if (!candidate) {
      setSrc(DEFAULT_COVER);
      return;
    }
    try {
      const tester = document.createElement('img');
      tester.onload = () => setSrc(candidate);
      tester.onerror = () => {
        // If template default fails, try our stock DEFAULT_COVER, then remote fallback as last resort
        const def = document.createElement('img');
        def.onload = () => setSrc(DEFAULT_COVER);
        def.onerror = () => setSrc(REMOTE_FALLBACK_COVER_URL);
        def.src = DEFAULT_COVER;
      };
      tester.src = candidate;
    } catch {
      // As a final catch-all, use remote fallback
      setSrc(REMOTE_FALLBACK_COVER_URL);
    }
  }, [variant, coverUrl]);
  return src;
};

import PhoneBezel from "./common/PhoneBezel";

/* ============ Templates (clean, predictable layout) ============ */

// --- Blocks-spec renderer (v2) ---
const BlocksSpecRenderer: React.FC<{ spec: TemplateSpec; user: PreviewUser; mode?: 'grid' | 'full' }>
 = ({ spec, user, mode = 'grid' }) => {
  const { openShare } = useShare();
  const [showSigModal, setShowSigModal] = React.useState(false);
  // Theme with safe fallbacks
  const theme = {
    bg: spec?.theme?.bg ?? '#FFFFFF',
    cardBg: spec?.theme?.cardBg ?? '#FFFFFF',
    text: spec?.theme?.text ?? '#0F172A',
    muted: spec?.theme?.muted ?? '#475569',
    accent: spec?.theme?.accent ?? '#123B8A',
    buttonBg: spec?.theme?.buttonBg ?? '#F1F5F9',
    buttonFg: spec?.theme?.buttonFg ?? '#0F1F33',
    saveBg: spec?.theme?.saveBg ?? '#FF7C67',
    saveFg: spec?.theme?.saveFg ?? '#FFFFFF',
    border: spec?.theme?.border ?? '#E6EDF5'
  } as const;
  const content = (spec?.content || {}) as any;

  // Header / cover
  const explicitCover = spec?.header?.coverUrl as (string | undefined);
  const headerType = (spec?.header?.type as string) || (explicitCover || user.coverUrl ? 'cover' : 'band');
  const objectPosition = (spec?.header?.objectPosition as string) || 'center 15%';
  const overlay = (spec as any)?.header?.overlay; // e.g. { from:'transparent', to:'rgba(255,255,255,.18)' }
  const variant = normalizeComponentKey(spec.componentKey ?? spec.variant ?? undefined) ?? 'elegant-looking';
  const ac = (spec.accent as string) || theme.accent || getDefaultAccent(variant);
  const bandColor = (spec?.header?.bandColor as string) || theme.accent;
  const height = Number(spec?.header?.height ?? 148);
  const radius = Number(spec?.header?.radius ?? 20);
  const cover = useResolvedHeader(variant, explicitCover || user.coverUrl);

  const av = spec?.header?.avatar || {};
  const avSize = Number(av.size ?? 112);
  const avOverlap = !!av.overlap;
  const avOffsetY = Number(av.offsetY ?? 24);
  const avRing = (av.border && typeof av.border === 'object') ? av.border : {};
  const avRingColor = (avRing as any).color || '#FFFFFF';
  const avRingWidth = Number((avRing as any).width ?? 0);
  const avShadow = av.shadow ?? true; // allow turning it off via spec.header.avatar.shadow=false
  const actions = (spec?.header?.actions?.buttons || []) as Array<any>;
  const actionHeight = Number(((spec?.header as any)?.actions?.height ?? 36));

  const gridCols = Number(spec?.grid?.columns ?? 2);
  const gridGap  = spec?.grid?.gap ?? 16;
  const blocks   = Array.isArray(spec?.blocks) ? spec.blocks : [];
  const cardRad  = Number(spec?.sectionCard?.radius ?? 14);
  const cardBd   = spec?.sectionCard?.border || { color: theme.border, width: 1 };
  const cardPad  = Number(spec?.sectionCard?.padding ?? 18);
  const colors = {
    pageBg: theme.bg,
    cardBg: theme.cardBg,
    text: theme.text,
    muted: theme.muted,
    buttonBg: theme.buttonBg,
    buttonFg: theme.buttonFg,
    saveBg: theme.saveBg,
    saveFg: theme.saveFg,
    border: theme.border,
  } as const;

  // Resolve owner/card context for email modal
  const ownerName = user.displayName || user.name || '';
  const ownerTitle = user.position || '';
  const ownerPhone = user.phone || '';
  const ownerWebsite = user.website || '';
  const handle = (user as any)?.handle || (user as any)?.username || '';
  const username = (user as any)?.username || '';

  return (
  <div className="h-full w-full flex flex-col" style={{ background: theme.bg, color: theme.text }}>
      {/* Header */}
      <div className="relative w-full" style={{ height }}>
        {headerType === 'cover' ? (
          <>
            <img
              src={cover}
              alt="cover"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ borderTopLeftRadius: radius, borderTopRightRadius: radius, objectPosition }}
              onError={(e) => {
                // Robust fallback: try stock cover, then remote image as last resort
                const img = e.currentTarget as HTMLImageElement;
                if (img.src.endsWith(DEFAULT_COVER)) {
                  img.src = REMOTE_FALLBACK_COVER_URL;
                } else {
                  img.src = DEFAULT_COVER;
                }
              }}
            />
            {overlay && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ borderTopLeftRadius: radius, borderTopRightRadius: radius,
                  background: `linear-gradient(to bottom, ${(overlay as any).from || 'transparent'}, ${(overlay as any).to || 'rgba(255,255,255,.18)'})` }}
              />
            )}
          </>
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: bandColor, borderTopLeftRadius: radius, borderTopRightRadius: radius }} />
        )}
      </div>

      {/* Avatar overlap */}
      {avOverlap && (
        <div className="flex justify-center" style={{ marginTop: -(avSize/2) + avOffsetY, zIndex: 5 }}>
          <div
            className="rounded-full overflow-hidden bg-white"
            style={{
              width: avSize, height: avSize,
              border: avRingWidth ? `${avRingWidth}px solid ${avRingColor}` : undefined,
              boxShadow: avShadow ? '0 6px 16px rgba(0,0,0,.12)' : undefined
            }}
          >
            <Avatar name={user.displayName || user.name} url={user.avatarUrl} round noBorder className="h-full w-full" />
          </div>
        </div>
      )}

      {/* Titles */}
      <div className="px-4 text-center mt-2">
        <div className="text-[32px] font-black" style={{ color: theme.text }}>
          {content.title ?? user.displayName ?? user.name ?? 'John Doe'}
        </div>
        <div className="text-[18px] font-semibold" style={{ color: theme.muted }}>
          {content.subtitle ?? user.position ?? 'Music Artist'}
        </div>
      </div>

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex items-center justify-center gap-3 mt-3 px-4">
      {actions.map((b: any, i: number) => (
            <button
              key={i}
              className="rounded-xl text-sm px-4 py-2"
              style={{
                background: b?.bg || theme.buttonBg,
                color: b?.fg || theme.buttonFg,
                border: `1px solid ${theme.border}`,
        // Keep all action buttons equal height; icon-only becomes a square using this height
        height: actionHeight,
        minHeight: actionHeight,
        minWidth: (b?.square || (!b?.label && (String(b?.key||'').toLowerCase()==='qr' || String(b?.key||'').toLowerCase()==='qrcode'))) ? actionHeight : undefined,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              onClick={() => {
                const title = content.title ?? user.displayName ?? user.name;
                if (b?.key === 'email') {
                  setShowSigModal(true);
                }
                if (b?.key === 'share') {
                  try {
                    const h = (user as any)?.handle || (user as any)?.username || '';
                    const un = (user as any)?.username || '';
                    const fallback = typeof window !== 'undefined' ? window.location.href : '';
                    const url = (h && un) ? canonicalCardUrl(h, un) : (user.website || fallback);
                    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
                    if ((navigator as any)?.share && isMobile) {
                      (navigator as any).share({ title, url }).catch(() => openShare(url));
                    } else {
                      openShare(url);
                    }
                  } catch { openShare(typeof window !== 'undefined' ? window.location.href : ''); }
                }
                if (b?.key === 'qr') {
                  try { document.dispatchEvent(new CustomEvent('tcard:openQR', { detail: { user } })); } catch {}
                }
              }}
            >
              {(() => {
                const key = String(b?.key || '').toLowerCase();
                const iconName = String(b?.icon || '').toLowerCase();
                const wantIcon = !!b?.icon || !!b?.iconSvg || (!b?.label && (key === 'qr' || key === 'qrcode' || key === 'email' || key === 'share'));
                const IconComp = (iconName === 'mail' || iconName === 'email' || key === 'email') ? Mail
                  : (iconName === 'share' || key === 'share') ? Share2
                  : (iconName === 'qr' || iconName === 'qrcode' || key === 'qr' || key === 'qrcode') ? QrCode
                  : null;
                return (
                  <span className="inline-flex items-center gap-2" style={{ color: b?.fg || theme.buttonFg }}>
                    {wantIcon && IconComp ? <IconComp className="w-4 h-4" aria-hidden /> : null}
                    {wantIcon && !IconComp && b?.iconSvg ? (
                      <span className="w-4 h-4 inline-block" aria-hidden dangerouslySetInnerHTML={{ __html: b.iconSvg }} />
                    ) : null}
                    {b?.label ?? ''}
                  </span>
                );
              })()}
            </button>
          ))}
        </div>
      )}

  {/* Grid blocks */}
      <div className="px-4 mt-4 grid"
           style={{ gridTemplateColumns:`repeat(${gridCols}, minmax(0,1fr))`, gap: (gridGap as number) }}>
        {blocks.filter(b=>['about','contact','social','streaming','products','services','vision-mission'].includes(String(b?.type))).map((b:any,i:number)=>(
          <div key={i}
               className="bg-white"
               style={{ background: theme.cardBg, borderRadius:cardRad, border:`${(cardBd as any).width}px solid ${(cardBd as any).color || theme.border}`,
                        padding:cardPad, gridColumn:`span ${Number(b?.span||1)}` }}>
            <div className="font-semibold mb-2" style={{ color: theme.text }}>{b?.title||b?.type}</div>
            {b?.type==='about' && (
              <div className="text-[13px] leading-snug" style={{ color: theme.muted }}>
                {(content.about ?? (user.bio || (mode==='grid' ? 'A short professional summary goes here.' : '')))}
              </div>
            )}
            {b?.type==='contact' && (
              <div className="text-[13px] space-y-1" style={{ color: theme.text }}>
                {(['phone','email','web','location'] as const).map((k) => {
                  const map: Record<string, string | undefined> = {
                    phone: user.phone,
                    email: user.email,
                    web: user.website,
                    location: user.location,
                  };
                  const v = map[k];
                  if (!v) return null;
                  return <div key={k} className={(k==='email' || k==='web') ? 'truncate' : ''}>{v}</div>;
                })}
              </div>
            )}
            {b?.type==='social' && (() => {
              const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
              const normalize = (s: string) => String(s || '').trim().toLowerCase();
              const alias = (p: string, url?: string) => {
                const k = normalize(p);
                const u = normalize(url || '');
                if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
                if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
                return k;
              };
              const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
              const raw = Object.entries((user as any).socialLinks || (user as any).social || {})
                .filter(([_, url]) => url && String(url).trim() !== '');
              const entries = raw
                .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
                .filter(([p]) => ALLOWED.has(String(p)));
              entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
              const limit = Number(b?.limit ?? 3);
              return (
      <div className="flex justify-start gap-3 flex-wrap">
                  {entries.slice(0, limit).map(([platform, url]) => (
                    <a key={String(platform)} href={String(url)} target="_blank" rel="noopener noreferrer"
        className="p-2 rounded-full shadow-sm border hover:shadow transition"
        style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                      <UnifiedBrandIcon platform={String(platform)} kind="social" size={20} title={String(platform)} className=""
                                        color={ac} />
                    </a>
                  ))}
                </div>
              );
            })()}
            {b?.type==='streaming' && (() => {
              const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
              const raw = Object.entries((user as any).streamingLinks || {})
                .filter(([platform, url]) => url && String(url).trim() !== '' && allowed.has(String(platform).toLowerCase()));
              if (!raw.length) return <div className="text-xs text-gray-400">No streaming links</div>;
              return (
         <div className="flex flex-wrap gap-2">
                  {raw.map(([platform, url]) => (
                    <a key={String(platform)} href={String(url)} target="_blank" rel="noopener noreferrer"
           className="flex items-center gap-1 text-xs px-2 py-1 rounded border"
           style={{ background: theme.cardBg, color: theme.text, borderColor: theme.border }}>
                      <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={14} title={String(platform)} className="" color={ac} />
                      {String(platform)}
                    </a>
                  ))}
                </div>
              );
            })()}
            {b?.type==='products' && (() => {
              const items = b?.items || [];
              const layout = b?.layout || 'grid'; // 'grid' or 'carousel'
              const gridCols = Number(b?.columns || 3);
              const autoScroll = b?.autoScroll !== false; // Default true for carousel
              const scrollSpeed = Number(b?.scrollSpeed || 3000); // 3 seconds per slide
              
              if (!items.length) return <div className="text-xs" style={{ color: theme.muted }}>No products available</div>;
              
              // Carousel layout with auto-scroll and swipe
              if (layout === 'carousel') {
                const carouselId = `carousel-${Math.random().toString(36).substr(2, 9)}`;
                
                React.useEffect(() => {
                  if (!autoScroll || items.length <= 1) return;
                  
                  const container = document.getElementById(carouselId);
                  if (!container) return;
                  
                  let currentIndex = 0;
                  const totalItems = items.length;
                  
                  const scroll = () => {
                    currentIndex = (currentIndex + 1) % totalItems;
                    const scrollAmount = container.scrollWidth / totalItems;
                    container.scrollTo({
                      left: scrollAmount * currentIndex,
                      behavior: 'smooth'
                    });
                  };
                  
                  const interval = setInterval(scroll, scrollSpeed);
                  
                  // Pause on hover
                  const pauseScroll = () => clearInterval(interval);
                  const resumeScroll = () => setInterval(scroll, scrollSpeed);
                  
                  container.addEventListener('mouseenter', pauseScroll);
                  container.addEventListener('mouseleave', resumeScroll);
                  
                  return () => {
                    clearInterval(interval);
                    container.removeEventListener('mouseenter', pauseScroll);
                    container.removeEventListener('mouseleave', resumeScroll);
                  };
                }, [autoScroll, scrollSpeed, items.length]);
                
                return (
                  <div className="relative -mx-4">
                    <div 
                      id={carouselId}
                      className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3 px-4"
                      style={{ 
                        scrollbarWidth: 'none',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      {items.map((p: any, idx: number) => (
                        <div 
                          key={idx} 
                          className="flex-shrink-0 snap-center"
                          style={{ width: 'calc(100% - 2rem)' }}
                        >
                          <div className="text-center">
                            {p?.image && (
                              <img 
                                src={normalizeUrl(p.image)} 
                                alt={p?.name || `Product ${idx + 1}`}
                                className="w-full h-48 object-cover rounded-lg mb-3"
                                style={{ backgroundColor: theme.border }}
                              />
                            )}
                            <div className="text-base font-bold mb-1" style={{ color: theme.text }}>
                              {p?.name || `Product ${idx + 1}`}
                            </div>
                            {p?.description && (
                              <div className="text-sm mt-1 px-2 line-clamp-2" style={{ color: theme.muted }}>
                                {p.description}
                              </div>
                            )}
                            {p?.price && (
                              <div className="text-lg font-bold mt-2" style={{ color: ac }}>
                                {p.price}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Pagination dots */}
                    <div className="flex justify-center gap-1.5 mt-3">
                      {items.map((_: any, idx: number) => (
                        <button
                          key={idx}
                          className="w-2 h-2 rounded-full transition-all"
                          style={{ 
                            backgroundColor: idx === 0 ? ac : `${theme.border}`,
                            opacity: idx === 0 ? 1 : 0.4
                          }}
                          onClick={() => {
                            const container = document.getElementById(carouselId);
                            if (container) {
                              const scrollAmount = container.scrollWidth / items.length;
                              container.scrollTo({
                                left: scrollAmount * idx,
                                behavior: 'smooth'
                              });
                            }
                          }}
                          aria-label={`Go to product ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                );
              }
              
              // Grid layout (default)
              return (
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
                  {items.map((p: any, idx: number) => (
                    <div key={idx} className="text-center">
                      {p?.image && (
                        <img 
                          src={normalizeUrl(p.image)} 
                          alt={p?.name || `Product ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg mb-2"
                          style={{ backgroundColor: theme.border }}
                        />
                      )}
                      <div className="text-sm font-semibold" style={{ color: theme.text }}>
                        {p?.name || `Product ${idx + 1}`}
                      </div>
                      {p?.description && (
                        <div className="text-xs mt-1" style={{ color: theme.muted }}>
                          {p.description}
                        </div>
                      )}
                      {p?.price && (
                        <div className="text-sm font-bold mt-1" style={{ color: ac }}>
                          {p.price}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
            {b?.type==='services' && (() => {
              const items = b?.items || [];
              const layout = b?.layout || 'grid'; // 'grid' or 'list'
              if (!items.length) return <div className="text-xs" style={{ color: theme.muted }}>No services available</div>;
              
              if (layout === 'list') {
                return (
                  <div className="space-y-3">
                    {items.map((s: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-3">
                        {s?.icon && (
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" 
                               style={{ backgroundColor: `${ac}15`, color: ac }}>
                            {s.iconSvg ? (
                              <span className="w-5 h-5" dangerouslySetInnerHTML={{ __html: s.iconSvg }} />
                            ) : (
                              <span className="text-xl">{s.icon}</span>
                            )}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-semibold text-sm" style={{ color: theme.text }}>
                            {s?.title || `Service ${idx + 1}`}
                          </div>
                          {s?.description && (
                            <div className="text-xs mt-1" style={{ color: theme.muted }}>
                              {s.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              
              // Grid layout (default)
              const gridCols = Number(b?.columns || 2);
              return (
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
                  {items.map((s: any, idx: number) => (
                    <div key={idx} className="text-center p-3 rounded-lg" style={{ backgroundColor: `${theme.border}30` }}>
                      {s?.icon && (
                        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2" 
                             style={{ backgroundColor: `${ac}20`, color: ac }}>
                          {s.iconSvg ? (
                            <span className="w-6 h-6" dangerouslySetInnerHTML={{ __html: s.iconSvg }} />
                          ) : (
                            <span className="text-2xl">{s.icon}</span>
                          )}
                        </div>
                      )}
                      <div className="font-semibold text-sm" style={{ color: theme.text }}>
                        {s?.title || `Service ${idx + 1}`}
                      </div>
                      {s?.description && (
                        <div className="text-xs mt-1" style={{ color: theme.muted }}>
                          {s.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>
      
      {/* Vision/Mission blocks - full width outside grid */}
      <div className="px-4 mt-4">
        {blocks.filter(b=>String(b?.type)==='vision-mission').map((b:any,i:number)=>(
          <div key={i} className="grid gap-3" 
               style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`, gridColumn: `span ${Number(b?.span||2)}` }}>
            {b?.vision && (
              <div className="rounded-xl p-4" 
                   style={{ background: b?.visionBg || ac, color: b?.visionFg || '#FFFFFF' }}>
                <div className="font-bold text-lg mb-2">{b?.visionTitle || 'Our Vision'}</div>
                <div className="text-sm leading-relaxed">{b?.vision}</div>
              </div>
            )}
            {b?.mission && (
              <div className="rounded-xl p-4" 
                   style={{ background: b?.missionBg || ac, color: b?.missionFg || '#FFFFFF' }}>
                <div className="font-bold text-lg mb-2">{b?.missionTitle || 'Our Mission'}</div>
                <div className="text-sm leading-relaxed">{b?.mission}</div>
              </div>
            )}
          </div>
        ))}
      </div>
  {/* share popup handled by global ShareProvider */}

      {/* Save + Ads */}
      <div className="px-4 mt-4">
        {blocks.filter(b=>String(b?.type)==='save').map((b:any,i:number)=>(
          <button key={i} className="w-full rounded-xl font-semibold py-3"
                  style={{ background:(b?.bg||theme.saveBg), color:(b?.fg||theme.saveFg) }}>
            {b?.label||content.saveLabel||'SAVE CARD'}
          </button>
        ))}
      </div>
      <div className="px-4 my-4">
        {blocks.filter(b=>String(b?.type)==='ads').map((b:any,i:number)=>(
          <div key={i} className="text-center py-5 text-[13px]"
               style={{ background: theme.cardBg, color: theme.text, borderRadius:cardRad, border:`${(cardBd as any).width}px solid ${(cardBd as any).color || theme.border}` }}>
            <div className="font-semibold">{b?.title||content.adsTitle||'ADS'}</div>
            <div className="text-xs" style={{ color: theme.muted }}>{b?.subtitle||content.adsSubtitle||'SPONSORED'}</div>
          </div>
        ))}
      </div>
      {/* Email Signature Modal */}
      <EmailSignatureModal
        isOpen={showSigModal}
        onClose={() => setShowSigModal(false)}
        handle={String(handle)}
        username={String(username)}
        ownerName={ownerName}
        ownerTitle={ownerTitle}
        ownerPhone={ownerPhone}
        ownerWebsite={ownerWebsite}
      />
    </div>
  );
};
// Minimal spec-driven renderer to support dynamic spec.json
const SpecRenderer: React.FC<{
  spec: TemplateSpec;
  user: PreviewUser;
  mode?: 'grid' | 'full';
  className?: string;
}> = ({ spec, user, mode = 'grid', className }) => {
  if (Array.isArray(spec?.blocks) && spec.blocks.length) {
    return <BlocksSpecRenderer spec={spec} user={user} mode={mode} />;
  }
  const v = normalizeComponentKey(spec.componentKey ?? spec.variant ?? undefined) ?? 'professional-classic';
  const ac = (spec.accent ?? undefined) || getDefaultAccent(v);
  const header = useResolvedHeader(v, user.coverUrl);

  const renderNode = (node: NonNullable<TemplateSpec['layout']>[number], idx: number): React.ReactNode => {
    switch (node.kind) {
      case 'text':
        return (
          <div key={idx} className={node.className ?? 'text-[13px] text-gray-800'}>
            {node.text || ''}
          </div>
        );
      case 'image': {
        const src = normalizeUrl(node.src) || user.avatarUrl || undefined;
        const round = node.round ?? false;
        return (
          <div key={idx} className={node.className ?? ''}>
            {src ? (
              <img src={src} alt="img" className={(round ? 'rounded-full' : 'rounded-xl') + ' w-full object-cover'} />
            ) : null}
          </div>
        );
      }
      case 'button':
        return (
          <button
            key={idx}
            className={node.className ?? 'rounded-full py-2 px-3 text-white text-sm'}
            style={{ backgroundColor: ac }}
            onClick={() => { /* noop in preview */ }}
          >
            {node.text || 'Action'}
          </button>
        );
      case 'row':
        return (
          <div key={idx} className={node.className ?? 'grid grid-cols-3 gap-3'}>
            {(node.children ?? []).map((c, i) => renderNode(c!, i))}
          </div>
        );
      case 'col': {
        const span = Math.max(1, Math.min(3, node.span ?? 1));
        return (
          <div key={idx} className={node.className ?? ''} style={{ gridColumn: `span ${span} / span ${span}` }}>
            {(node.children ?? []).map((c, i) => renderNode(c!, i))}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className={className}>
      <div className="relative w-full" style={{ height: HEADER_HEIGHT }}>
        <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 15%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/20 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      {ac && <div className="h-2 w-full" style={{ backgroundColor: ac }} />}
      <div className="p-4 space-y-3">
        {(spec.layout ?? [
          { kind: 'text', text: user.displayName || user.name || 'Your Name', className: 'text-[17px] font-semibold text-gray-900' },
          { kind: 'text', text: user.position || 'Role', className: 'text-sm text-gray-600' },
          { kind: 'text', text: user.company || '', className: 'text-xs text-gray-500' },
          { kind: 'text', text: user.bio || (mode === 'grid' ? 'A short professional summary goes here.' : ''), className: 'text-[13px] text-gray-700' },
        ]).map((n, i) => renderNode(n!, i))}
      </div>
    </div>
  );
};

const Classic: React.FC<PreviewCardProps> = ({ user, accent, mode = "grid", variant }) => {
  const ac = accent || getDefaultAccent(variant || "professional-classic");
  const header = useResolvedHeader("professional-classic", user.coverUrl);
  // In grid mode (template selection) give the professional classic template a taller header to match 'Elegant Looking'
  const headerHeight = mode === "grid" ? HEADER_HEIGHT + 64 : HEADER_HEIGHT;
  if (import.meta.env.DEV) {
    // helpful debug while developing to ensure header URL resolves as expected
     
    console.debug('[PreviewCard:Classic] header ->', header, 'mode ->', mode, 'headerHeight ->', headerHeight);
  }
  return (
    <div className="h-full w-full flex flex-col">
      {/* Header */}
  <div className="relative w-full" style={{ height: headerHeight }}>
    {/* cover image rendered as img to ensure visible inside bezel with robust fallback */}
  <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 15%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
        {/* stronger gradient so thin cover separator remains visible */}
  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/20 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      {/* Accent ribbon */}
      <div className="h-2 w-full" style={{ backgroundColor: ac }} />
      {/* Identity */}
  <div className="px-4 pt-10">
        <div className="flex items-center gap-3">
          <div className="relative z-30">
            <Avatar name={user.displayName || user.name} url={user.avatarUrl} />
          </div>
          <div className="min-w-0">
            <div className="text-[17px] font-semibold text-gray-900 truncate">{user.displayName || user.name || "Alex Thompson"}</div>
            <div className="text-sm text-gray-600 truncate">
              {(user.position || "UX Designer") + (user.company ? ` at ${user.company}` : " at Design Studio Pro")}
            </div>
          </div>
        </div>
      </div>

  {/* Quick actions - allow wrap so long labels don't overflow */}
  <div className="px-4 pt-3 flex flex-wrap gap-2">
        {["Call", "Email", "Website", "QR Code", "Share", "Broadcast"].map((t) => (
          <div key={t} className="rounded-xl border bg-white py-1 px-2 text-center text-xs text-gray-700 whitespace-nowrap">
            {t}
          </div>
        ))}
      </div>

      {/* About */}
  {/* Show sample About in grid mode even if bio is missing so section is visible */}
  {(user.bio || true) && (
      <div className="px-4 pt-2 pb-2">
        <div className="rounded-xl border bg-white p-3 text-[13px] text-gray-700 leading-snug">{user.bio || (mode === 'grid' ? 'A short professional summary goes here.' : '')}</div>
      </div>
  )}

      {/* Contact */}
      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Contact</div>
          <div className="text-gray-700 truncate">{user.phone || "+1 (555) 321-9876"}</div>
          <div className="text-gray-700 truncate">{user.email || "alex.thompson@designstudio.com"}</div>
          <div className="text-gray-700 truncate">{user.website || ''}</div>
          <div className="text-gray-700 truncate">{user.location || "Dar es Salaam, Tanzania"}</div>
        </div>
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Social</div>
          <div className="text-gray-700">LinkedIn</div>
          <div className="text-gray-700">Twitter/X</div>
          <div className="text-gray-700">Instagram</div>
        </div>
      </div>

      {mode === "full" && (
        <div className="px-4 pb-4">
          <button className="w-full rounded-full py-3 font-semibold text-white shadow" style={{ backgroundColor: ac }}>
            Save to Contacts
          </button>
        </div>
      )}
    </div>
  );
};

const Elegant: React.FC<PreviewCardProps> = ({ user, accent, mode = "grid" }) => {
  const ac = accent || getDefaultAccent("elegant-looking");
  const header = useResolvedHeader("elegant-looking", user.coverUrl);
  // Give the cover more visibility in grid mode
  const headerHeight = mode === "grid" ? HEADER_HEIGHT + 32 : HEADER_HEIGHT;
  return (
    <div className="h-full w-full flex flex-col">
    <div className="relative w-full" style={{ height: headerHeight }}>
  <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 22%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/10 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      {/* small accent bar under header */}
      <div className="h-2 w-full" style={{ backgroundColor: ac }} />
  <div className="px-4 mt-5 flex flex-col items-center">
        <div className="rounded-full overflow-hidden shadow bg-white p-1.5 relative z-30">
          <Avatar name={user.displayName || user.name} url={user.avatarUrl} round noBorder className="h-16 w-16" />
        </div>
        <div className="mt-3 text-[18px] font-semibold text-gray-900">{user.displayName || user.name || "Sarah Chen"}</div>
        <div className="text-sm text-gray-600">{user.position || "Marketing Director"}</div>
        <div className="text-xs text-gray-500 italic">{user.company || "InnovateCorp"}</div>
      </div>

      <div className="px-4 pt-3 flex flex-wrap gap-2">
        {["Call", "Email", "Website", "QR Code", "Share", "Broadcast"].map((t) => (
          <div key={t} className="rounded-2xl text-center text-xs py-1 px-2 whitespace-nowrap" style={{ backgroundColor: `${ac}1A`, border: `1px solid ${ac}33`, color: "#374151" }}>
            {t}
          </div>
        ))}
      </div>
      {/* About */}
  {(user.bio || true) && (
        <div className="px-4 pt-3">
          <div className="rounded-2xl p-3 text-[13px] text-gray-700" style={{ background: "#FFF7F9", border: "1px solid #FCE7F3" }}>
    {user.bio || (mode === 'grid' ? 'A short professional summary goes here.' : '')}
          </div>
        </div>
      )}

      {/* Contact + Social */}
      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Contact</div>
          <div className="text-gray-700 truncate">{user.phone || "+1 (555) 321-9876"}</div>
          <div className="text-gray-700 truncate">{user.email || "sarah.chen@innovatecorp.com"}</div>
          <div className="text-gray-700 break-all">{user.website || ''}</div>
          <div className="text-gray-700 truncate">{user.location || "Dar es Salaam, Tanzania"}</div>
        </div>
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Social</div>
          <div className="text-gray-700">LinkedIn</div>
          <div className="text-gray-700">Twitter/X</div>
          <div className="text-gray-700">Instagram</div>
        </div>
      </div>
      {/* Save Card - visible in grid to highlight key action */}
      <div className="px-4 pb-4">
        <button className="w-full rounded-full py-2.5 font-semibold text-white shadow" style={{ backgroundColor: ac }}>
          Save Card
        </button>
      </div>
    </div>
  );
};

const Golden: React.FC<PreviewCardProps> = ({ user, accent, mode = "grid" }) => {
  const ac = accent || getDefaultAccent("golden-view");
  const header = useResolvedHeader("golden-view", user.coverUrl);
  return (
    <div className="h-full w-full flex flex-col">
    <div className="relative w-full" style={{ height: GOLDEN_HEADER_HEIGHT }}>
  <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 15%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
  {/* Golden uses a subtle amber overlay to tint the cover; layering overlay only */}
  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/12 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      <div className="h-2 w-full" style={{ backgroundColor: ac }} />
      <div className="px-4 pt-6">
        <div className="flex items-center gap-3">
          <div className="relative z-30">
            <Avatar name={user.displayName || user.name} url={user.avatarUrl} />
          </div>
          <div className="min-w-0">
            <div className="text-[17px] font-semibold text-gray-900 truncate">{user.displayName || user.name || "Emma Wilson"}</div>
            <div className="text-sm text-gray-600 truncate">
              {(user.position || "Creative Director") + (user.company ? ` @ ${user.company}` : " @ Golden Design Studio")}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 grid grid-cols-3 gap-3">
        {["Call", "Email", "Website", "QR Code", "Share", "Broadcast"].map((t) => (
          <div key={t} className="rounded-xl border bg-white py-1.5 text-center text-xs text-gray-700 whitespace-nowrap">{t}</div>
        ))}
      </div>
      {/* About */}
  {(user.bio || true) && (
        <div className="px-4 pt-3">
          <div className="rounded-xl p-3 text-[13px] text-gray-800" style={{ background: `${ac}14`, border: `1px solid ${ac}33` }}>
    {user.bio || (mode === 'grid' ? 'A short professional summary goes here.' : '')}
          </div>
        </div>
      )}

      {/* Contact + Social */}
      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Contact</div>
          <div className="text-gray-700 truncate">{user.phone || "+1 (555) 987-6543"}</div>
          <div className="text-gray-700 truncate">{user.email || "emma.wilson@goldenstudio.com"}</div>
          <div className="text-gray-700 break-all">{user.website || ''}</div>
          <div className="text-gray-700 truncate">{user.location || "Dar es Salaam, Tanzania"}</div>
        </div>
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Social</div>
          <div className="text-gray-700">LinkedIn</div>
          <div className="text-gray-700">Twitter/X</div>
          <div className="text-gray-700">Instagram</div>
        </div>
      </div>
    </div>
  );
};

const Modern: React.FC<PreviewCardProps> = ({ user, accent, mode = "grid" }) => {
  const ac = accent || getDefaultAccent("modern-clean");
  const header = useResolvedHeader("modern-clean", user.coverUrl);
  return (
    <div className="h-full w-full flex flex-col">
    <div className="relative w-full" style={{ height: HEADER_HEIGHT }}>
  <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 15%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/20 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      <div className="h-2 w-full" style={{ backgroundColor: ac }} />
  <div className="px-4 pt-5">
        <div className="flex items-center gap-3">
          <div className="relative z-30">
            <Avatar name={user.displayName || user.name} url={user.avatarUrl} />
          </div>
          <div>
            <div className="text-[17px] font-semibold text-gray-900">{user.displayName || user.name || "Michael Rodriguez"}</div>
            <div className="text-sm text-gray-600">{user.position || "Product Manager"}</div>
            <div className="text-xs text-gray-500">{user.company || "TechFlow Solutions"}</div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 grid grid-cols-3 gap-3">
        {["Call", "Email", "Website", "QR", "Share", "Cast"].map((t) => (
          <div
            key={t}
            className="rounded-xl py-1.5 text-center text-xs whitespace-nowrap"
            style={{ background: `${ac}14`, border: `1px solid ${ac}33`, color: "#064E3B" }}
          >
            {t}
          </div>
        ))}
      </div>

  <div className="px-4 py-4 space-y-3">
        {user.bio ? (
          <div className="rounded-xl p-3 text-[13px]" style={{ background: "#ECFDF5", color: "#164E3B" }}>
            {user.bio}
          </div>
        ) : (
          // Show About placeholder in grid mode for visual completeness
          (mode === 'grid' ? <div className="rounded-xl p-3 text-[13px]" style={{ background: "#ECFDF5", color: "#164E3B" }}>A short professional summary goes here.</div> : null)
        )}
        <div className="rounded-xl p-3 text-[13px] border">
          <div className="text-gray-800">{user.phone || "+1 (555) 456-7890"}</div>
          <div className="text-gray-800 truncate">{user.email || "michael.rodriguez@techflow.com"}</div>
          <div className="text-gray-800 break-all">{user.website || ''}</div>
        </div>
        {/* Social block for consistency */}
        <div className="rounded-xl p-3 text-[13px] border">
          <div className="text-gray-900 font-medium mb-1">Social</div>
          <div className="text-gray-800">LinkedIn</div>
          <div className="text-gray-800">Twitter/X</div>
          <div className="text-gray-800">Instagram</div>
        </div>
      </div>

      {mode === "full" && (
        <div className="px-4 pb-4">
          <button className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: ac }}>
            Save Contact
          </button>
        </div>
      )}
    </div>
  );
};

const Modular: React.FC<PreviewCardProps> = ({ user, accent, mode = "grid" }) => {
  const ac = accent || getDefaultAccent("classic-modular");
  const header = useResolvedHeader("classic-modular", user.coverUrl);
  return (
    <div className="h-full w-full flex flex-col">
      <div className="relative w-full" style={{ height: HEADER_HEIGHT }}>
        <img src={header} alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0, objectPosition: 'center 15%' }} onError={(e) => { const img = e.currentTarget as HTMLImageElement; if (img.src !== DEFAULT_COVER) { img.src = DEFAULT_COVER; } else { img.src = REMOTE_FALLBACK_COVER_URL; } }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/20 pointer-events-none" style={{ zIndex: 1 }} />
      </div>
      <div className="h-2 w-full" style={{ backgroundColor: ac }} />
      <div className="px-4 pt-4">
        <div className="flex items-center gap-3">
          <Avatar name={user.displayName || user.name} url={user.avatarUrl} />
          <div className="min-w-0">
            <div className="text-[17px] font-semibold text-gray-900 truncate">{user.displayName || user.name || "Alex Johnson"}</div>
            <div className="text-sm text-gray-600 truncate">{(user.position || "Designer") + (user.company ? ` @ ${user.company}` : "")}</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Call","Email","QR","Share"].map(t => (
            <div
              key={t}
              className="h-8 px-3 inline-flex items-center justify-center rounded-full border bg-white text-xs text-gray-700 whitespace-nowrap"
              style={{ borderColor: `${ac}66` }}
            >
              {t}
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 pt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">About</div>
          <div className="text-gray-700 leading-snug">{user.bio || (mode === 'grid' ? 'A short professional summary goes here.' : '')}</div>
        </div>
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-1">Contact</div>
          <div className="text-gray-700 truncate">{user.phone || "+1 (555) 123-4567"}</div>
          <div className="text-gray-700 truncate">{user.email || "alex.johnson@example.com"}</div>
          <div className="text-gray-700 break-all">{user.website || ''}</div>
        </div>
      </div>
      <div className="px-4 pt-3 pb-4">
        <div className="rounded-xl border bg-white p-3 text-[13px]">
          <div className="text-gray-900 font-medium mb-2">Social</div>
          <div className="flex flex-wrap gap-2">
            {["LinkedIn","Twitter/X","Instagram"].map(t => (
              <div
                key={t}
                className="h-7 px-3 inline-flex items-center justify-center rounded-full border bg-white text-[12px] text-gray-800"
                style={{ borderColor: `${ac}33` }}
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
      {mode === 'full' && (
        <div className="px-4 pb-4">
          <button className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: ac }}>
            Save Contact
          </button>
        </div>
      )}
    </div>
  );
};

/* ============ Public component ============ */

export const PreviewCard: React.FC<PreviewCardProps> = ({
  variant,
  accent,
  user,
  mode = "grid",
  className,
  templateMeta,
  templateSpec,
}) => {
  const fromMeta = React.useMemo(() => resolveFromMeta(templateMeta), [templateMeta]);
  // Prefer meta.componentKey/variant when present (maps dynamic names like "soft-blossom" to the closest static skin).
  const variantKey = React.useMemo(() => {
    const metaKey = (templateMeta?.componentKey ?? templateMeta?.variant) as (string | undefined);
    if (metaKey && String(metaKey).trim() !== '') {
      return resolveVariant(String(metaKey));
    }
    // Fallback to prop-based variant using shared normalizer
    const ensured = ensureTemplateVariant(variant, variant);
    return ensured ?? 'professional-classic';
  }, [templateMeta?.componentKey, templateMeta?.variant, variant]);

  // compute a responsive screenHeight in grid mode
  const [vw, setVw] = React.useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);
  React.useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  let screenHeight: number | undefined;
  if (mode !== 'grid') {
    screenHeight = undefined; // auto-size for full/live previews
  } else if (vw <= 480) {
    screenHeight = GRID_SCREEN_HEIGHT_MOBILE;
  } else if (vw <= 900) {
    screenHeight = GRID_SCREEN_HEIGHT_TABLET;
  } else {
    screenHeight = GRID_SCREEN_HEIGHT;
  }

  // In grid mode inside selection tiles, make the bezel fill the available box
  const bezelClass = `${className || ''} ${mode === 'grid' ? 'w-full h-full' : ''}`.trim();
  return (
    <PhoneBezel className={bezelClass} screenHeight={mode === 'grid' ? undefined : screenHeight}>
      {templateSpec ? (
        <SpecRenderer spec={templateSpec} user={user} mode={mode} />
      ) : !variantKey ? (
        (() => {
          if ((import.meta as any).env?.DEV) {
             
            console.warn("[PreviewCard] Missing/unknown variant:", variant);
          }
          return (
            <div className="h-full w-full flex items-center justify-center">
              <div className="rounded-lg border p-4 text-sm text-red-600 bg-red-50">
                This template is missing a valid variant. Ask an admin to fix the bundle’s componentKey (or variant).
              </div>
            </div>
          );
        })()
      ) : (
        <>
          {variantKey === "professional-classic" && <Classic variant={variantKey} user={user} accent={accent ?? fromMeta?.accent} mode={mode} />}
          {variantKey === "elegant-looking" && <Elegant variant={variantKey} user={user} accent={accent ?? fromMeta?.accent} mode={mode} />}
          {variantKey === "golden-view" && <Golden variant={variantKey} user={user} accent={accent ?? fromMeta?.accent} mode={mode} />}
          {variantKey === "modern-clean" && <Modern variant={variantKey} user={user} accent={accent ?? fromMeta?.accent} mode={mode} />}
          {variantKey === "classic-modular" && <Modular variant={variantKey} user={user} accent={accent ?? fromMeta?.accent} mode={mode} />}
        </>
      )}
    </PhoneBezel>
  );
};

/** Map a raw componentKey/name into a canonical PreviewVariant */
export const resolveVariant = (raw?: string): PreviewVariant => {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "professional-classic";

  // 1) exact match first
  if (KNOWN_VARIANTS.includes(s as PreviewVariant)) return s as PreviewVariant;

  // 2) prefix/alias normalization (handles "classic-modular-<hash>")
  const byPrefix: Array<[string | RegExp, PreviewVariant]> = [
    [/^professional-classic(?:[-_].+)?$/, "professional-classic"],
    [/^elegant-looking(?:[-_].+)?$/, "elegant-looking"],
    [/^modern-clean(?:[-_].+)?$/, "modern-clean"],
    [/^golden-view(?:[-_].+)?$/, "golden-view"],
    [/^classic-modular(?:[-_].+)?$/, "classic-modular"],
  ];
  for (const [rx, v] of byPrefix) if ((rx as RegExp).test(s)) return v;

  // 3) existing aliases
  if (ALIASES[s]) return ALIASES[s];

  // 4) loose text matches
  if (/soft[-_\s]?blossom/.test(s)) return "elegant-looking";
  if (/classic.*modular|modular|grid|blocks/.test(s)) return "classic-modular";
  if (/modern.*clean|modern/.test(s)) return "modern-clean";
  if (/gold(en)?/.test(s)) return "golden-view";
  if (/elegant/.test(s)) return "elegant-looking";

  if (import.meta.env.DEV) {
    console.warn(`[resolveVariant] Unknown componentKey "${raw}" — falling back to 'professional-classic'.`);
  }
  return "professional-classic";
};
